import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../shared/material.module';
import {
  CardsService,
  CardCreateRequest,
  BusinessError,
  social_links,
  quick_links,
  Entity,
  Leader,
  Permissions,
   Branches
} from '../../services/cards.service';
import { AuthService } from '../../services/auth.service';
import { DigitalCard } from '../../services/cards.service';
import { ThemePickerComponent } from '../theme/theme-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { LANGUAGE_OPTIONS, CARD_TYPE_OPTIONS, INDUSTRY_OPTIONS } from '../../constants/app.constants';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';

type ViewMode = 'grid' | 'list';



@Component({
  selector: 'app-edit-card',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, RouterModule, BreadcrumbComponent],
  templateUrl: './edit-card.component.html',
  styleUrls: ['./edit-card.component.scss'],
})
export class EditCardComponent implements OnInit {

  card!: DigitalCard | null;
  loading = false;
  error: string | null = null;

  businessSaving = false;
  contactSaving = false;
  cardSaving = false;


  logoPreview: string | null = null;
  logoShape: string | null = null;

  uploadProgress = 0;
  uploadingLogo = false;


  quickLinks: quick_links[] = [];
  socialLinks: social_links[] = [];

  // Dropdown open state for quick/social link UI
  linksOpen = { quick: false, social: false };

  /* ------------------------------------------------------------------------ */
  /*                           Form value containers                           */
  /* ------------------------------------------------------------------------ */

  contactFormValues = {
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyWebsite: '',
    address: '',
    description: '',
  };

  businessFormValues = {
    name: '',
    email: '',
    phone: '',
    department: '',
    jobTitle: '',
    whatsappNumber: '',
  };

  cardFormValues = {
    businessName: '',
    type: 'Business',
    industry: '',
    handler: '',
    language: 'en',
  };

  permissions: Permissions = {
    show_Analytical: 'show',
    Show_Payment_Methods: 'show',
  };

  entities: Entity[] = [{ name: '', domainUrl: '' }];

  leaders: Leader[] = [{ name: '', role: '' }];

  branches: Branches[] = [{ city: '', address: '', pincode: '' }];

  expandedSection:
    | 'contact'
    | 'card'
    | 'business'
    | 'socialLinks'
    | 'quickLinks'
    | 'logo'
    | 'theme'
    | 'delete'
    | 'entities'
    | 'leaders'
    | 'permissions'
    | 'branches'
    | null = null;

  sectionsOpen = {
    contact: true,
    card: true,
    business: true,
    quickLinks: true,
    socialLinks: true,
  };


  languageOptions = LANGUAGE_OPTIONS;
  cardTypeOptions = CARD_TYPE_OPTIONS;
  industryOptions = INDUSTRY_OPTIONS;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cardsService: CardsService,
    private authService: AuthService,
    private dialog: MatDialog,
    private storageService: FirebaseStorageService
  ) {}


  ngOnInit() {
    // Load card at initialization
    this.loadCard();

    // touch the getter to ensure preview logic is ready (no-op but kept as before)
    this.logoUrl;
  }

  async loadCard() {
    this.loading = true;
    this.error = null;

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = 'Invalid card ID';
      this.loading = false;
      return;
    }

    try {
      const detailed = await this.cardsService.getCardById(id);
      this.card = detailed || null;

      if (!this.card) {
        this.error = 'Card not found';
        return;
      }

      // ====== Contact Form ======
      this.contactFormValues = {
        companyName: this.card.business_info?.businessName || '',
        companyEmail: this.card.business_info?.companyEmail || '',
        companyPhone: this.card.business_info?.companyPhone || '',
        companyWebsite: this.card.business_info?.companyWebsite || '',
        address: this.card.business_info?.companyAddress || '',
        description: this.card.business_info?.description || '',
      };

      // ====== Card Form ======
      this.cardFormValues = {
        businessName: this.card.business_info?.businessName || '',
        type: this.card.type || 'Business',
        industry: this.card.industry || '',
        handler: this.card.handler || '',
        language: this.card.language || 'en',
      };

      // ====== Business Form (contact_info) ======
      // ensure business_info exists
      this.card.business_info = this.card.business_info || {};
      this.businessFormValues = {
        name: this.card.contact_info?.contactName || '',
        email: this.card.contact_info?.primaryEmail || '',
        phone: this.card.contact_info?.primaryPhone || '',
        department: this.card.contact_info?.department || '',
        jobTitle: this.card.contact_info?.jobTitle || '',
        whatsappNumber: this.card.contact_info?.whatsappNumber || '',
      };

      // logo shape fallback
      this.logoShape = this.card.business_info.logoShape || 'square';

      // ====== Quick & Social Links ======
      this.quickLinks = this.card.quick_links ? [...this.card.quick_links] : [];
      this.socialLinks = this.card.social_links ? [...this.card.social_links] : [];

      // Ensure at least one row exists for new cards
      if (this.quickLinks.length === 0) this.quickLinks.push({ title: '', url: '' });
      if (this.socialLinks.length === 0) this.socialLinks.push({ platform: '', url: '' });

      // Auto-expand sections if existing links
      if (this.quickLinks.some((l) => l.title || l.url)) {
        this.expandedSection = 'quickLinks';

      } else if (this.socialLinks.some((s) => s.platform || s.url)) {
        this.expandedSection = 'socialLinks';
      }
      this.entities = Array.isArray(this.card.entities) ? [...this.card.entities] : [];
if (this.entities.length === 0) {
  this.entities.push({ name: '', domainUrl: '' });
}
            // ====== Leaders ======
      this.leaders = Array.isArray(this.card.leaders) ? [...this.card.leaders] : [];
      if (this.leaders.length === 0) {
        this.leaders.push({ name: '', role: '' });
      }

      // ====== Permissions ======
      this.permissions = this.card.permissions || {
        show_Analytical: 'show',
        Show_Payment_Methods: 'show',
      };

      // ====== Branches ======
      this.branches = Array.isArray(this.card.branches) ? [...this.card.branches] : [];
      if (this.branches.length === 0) {
        this.branches.push({ city: '', address: '', pincode: '' });
      }

    } catch (err: any) {
      this.error = err.message || 'Failed to load card';
    } finally {
      this.loading = false;
    }
  }



  get logoUrl(): string | null {
    return this.logoPreview || this.card?.business_info?.companyLogo || null;
  }

  /**
   * Handle logo file selection, preview, upload to Firebase Storage,
   * update Firestore and local state. Preserves original console logs / alerts.
   */
  async onLogoSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.card) return;

    // Preview selected logo immediately
    const reader = new FileReader();
    reader.onload = () => (this.logoPreview = reader.result as string);
    reader.readAsDataURL(file);

    try {
      this.uploadingLogo = true;
      this.uploadProgress = 0;

      console.log('Uploading logo to Firebase Storage...');
      const cardId = this.card.id;

      // Upload with progress tracking
      const downloadUrl = await this.storageService.uploadFile(`cards/${cardId}/logo`, file, (percent) =>
        (this.uploadProgress = percent)
      );

      this.uploadingLogo = false;
      console.log('✅ Logo uploaded successfully:', downloadUrl);

      // Update Firestore with new logo URL
      const payload = {
        ...this.card.business_info,
        companyLogo: downloadUrl, // store the logo URL
      };

      await this.cardsService.updateCardBusinessInfo(cardId, payload);

      // Update local state
      this.card.business_info = payload;

      alert('✅ Logo updated successfully!');
    } catch (error: any) {
      this.uploadingLogo = false;
      console.error('❌ Failed to upload logo:', error);
      alert('Failed to upload logo: ' + (error.message || 'Unknown error'));
    }
  }

  /**
   * Persist selected logo shape in business_info on Firestore and local state.
   */
  async saveLogoShape() {
    if (!this.card) return;

    // Ensure business_info exists
    if (!this.card.business_info) {
      this.card.business_info = {};
    }

    this.businessSaving = true;

    try {
      const payload = {
        ...this.card.business_info,
        logoShape: this.logoShape, // use the component property directly
      };

      await this.cardsService.updateCardBusinessInfo(this.card.id, payload);

      // Update local state
      this.card.business_info.logoShape = this.logoShape;

      console.log('✅ Logo shape updated:', this.logoShape);
    } catch (err: any) {
      console.error('❌ Failed to update logo shape:', err);
      alert('Failed to update logo shape: ' + (err.message || 'Unknown error'));
    } finally {
      this.businessSaving = false;
    }
  }



  addQuickLink() {
    this.quickLinks.push({ title: '', url: '' } as quick_links);
  }

  removeQuickLink(index: number) {
    if (index >= 0 && index < this.quickLinks.length) this.quickLinks.splice(index, 1);
  }

  addSocialLink() {
    this.socialLinks.push({ platform: '', url: '' } as social_links);
  }

  removeSocialLink(index: number) {
    if (index >= 0 && index < this.socialLinks.length) this.socialLinks.splice(index, 1);
  }

  async saveQuickLinks() {
    if (!this.card) return;
    try {
      await this.cardsService.updateCardQuickLinks(this.card.id, this.quickLinks);
      console.log(this.quickLinks);
      console.log('✅ Quick links saved');
      // refresh local copy
      await this.loadCard();
    } catch (err: any) {
      console.error('❌ Failed to save quick links:', err);
      alert('Failed to save quick links: ' + (err.message || err));
    }
  }

  async saveSocialLinks() {
    if (!this.card) return;
    try {
      await this.cardsService.updateCardSocialLinks(this.card.id, this.socialLinks as social_links[]);
      console.log('✅ Social links saved');
      await this.loadCard();
    } catch (err: any) {
      console.error('❌ Failed to save social links:', err);
      alert('Failed to save social links: ' + (err.message || err));
    }
  }


  addEntity() {
    this.entities.push({ name: '', domainUrl: '' });
  }

  removeEntity(index: number) {
    this.entities.splice(index, 1);
  }

  async saveEntities() {
    if (!this.card) return;

    try {
      const payload = { entities: this.entities }; // ✅ wrap inside "entities"

      await this.cardsService.updatecardEntities(this.card.id, payload);

      console.log('✅ Entities payload sent:', payload);

      await this.loadCard(); // refresh after saving
    } catch (err: any) {
      console.error('❌ Failed to save entities:', err);
      alert('❌ Failed to save entities: ' + (err.message || err));
    }
  }

  addLeader() {
    this.leaders.push({ name: '', role: '' });
  }

  removeLeader(index: number) {
    this.leaders.splice(index, 1);
  }

  async saveLeaders() {
    if (!this.card) return;
    try {
      const payload = { leaders: this.leaders }; // ✅ wrap inside "leaders"

      await this.cardsService.updateCardLeaders(this.card.id, payload);

      console.log('✅ Leaders payload sent:', payload);

      await this.loadCard(); // refresh card data
    } catch (err: any) {
      console.error('❌ Failed to save leaders:', err);
      alert('❌ Failed to save leaders: ' + (err.message || err));
    }
  }

  addBranch() {
    this.branches.push({ city: '', address: '', pincode: '' });
  }

  removeBranch(index: number) {
    this.branches.splice(index, 1);
  }

  async saveBranches() {
    if (!this.card) return;

    try {
      const payload = { branches: this.branches }; // ✅ wrap data under "branches"

      await this.cardsService.updateCardBranchess(this.card.id, payload);

      console.log('✅ Branches payload sent successfully:', payload);

      await this.loadCard(); // ✅ refresh card data if already retrieving branches
    } catch (err: any) {
      console.error('❌ Failed to save branches:', err);
      alert('❌ Failed to save branches: ' + (err.message || err));
    }
  }


  onPermissionToggle(field: keyof Permissions, checked: boolean) {
    this.permissions[field] = checked ? 'show' : 'hide';
  }

  async savePermissions() {
    if (!this.card) return;

    try {
      const payload = { permissions: this.permissions };
      console.log('Saving permissions with payload:', payload);
      await this.cardsService.updateCardPermissions(this.card.id, payload);
      console.log('✅ Permissions saved successfully:', this.permissions);

      await this.loadCard(); // refresh updated data
    } catch (err: any) {
      console.error('❌ Failed to save permissions:', err);
      alert('❌ Failed to save permissions: ' + (err.message || err));
    }
  }


  async saveContact() {
    if (!this.card) return;

    this.contactSaving = true

    try {
      const updatedBusinessInfo = {
        ...this.card.business_info,
        businessName: this.contactFormValues.companyName.trim(),
        companyEmail: this.contactFormValues.companyEmail.trim(),
        companyPhone: this.contactFormValues.companyPhone.trim(),
        companyWebsite: this.contactFormValues.companyWebsite.trim(),
        companyAddress: this.contactFormValues.address.trim(),
        description: this.contactFormValues.description.trim(),
      };

      // 🔄 Save to backend using your Business Info API
      await this.cardsService.updateCardBusinessInfo(this.card.id, updatedBusinessInfo);

      // 💾 Update local state
      this.card.business_info = updatedBusinessInfo;

      console.log('✅ Business info saved successfully');
    } catch (err: any) {
      console.error('❌ Failed to save business info:', err);
      alert('Failed to save business info: ' + (err.message || 'Unknown error'));
    } finally {
      this.contactSaving = false;
    }
  }

  addPrefix() {
    const value = this.contactFormValues.companyPhone || '';
    if (!value.startsWith('+91')) {
      this.contactFormValues.companyPhone = '+91' + value.replace(/^(\+91)?/, '');
    }
  }

  async saveCard() {
    if (!this.card) return;

    this.cardSaving = true;

    try {
      await this.cardsService.updateCardBusinessInfo(this.card.id, {
        businessName: this.cardFormValues.businessName.trim(),
        companyEmail: this.card.business_info?.companyEmail?.trim(),
        companyPhone: this.card.business_info?.companyPhone?.trim(),
        companyWebsite: this.card.business_info?.companyWebsite?.trim(),
        companyAddress: this.card.business_info?.companyAddress?.trim(),
        description: this.card.business_info?.description?.trim(),
      });

      // Update local state
      this.card.business_info = {
        businessName: this.cardFormValues.businessName.trim(),
      };

      console.log('✅ Card info saved');
    } catch (err: any) {
      console.error('❌ Failed to save card info:', err);
      alert('Failed to save card info: ' + (err.message || 'Unknown error'));
    } finally {
      this.cardSaving = false;
    }
  }

  async saveBusinessInfo() {
    if (!this.card) return;

    this.businessSaving = true;

    try {
      const payload = {
        ...this.card.contact_info,
        contactName: this.businessFormValues.name.trim(),
        primaryEmail: this.businessFormValues.email.trim(),
        primaryPhone: this.businessFormValues.phone.trim(),
        department: this.businessFormValues.department.trim(),
        jobTitle: this.businessFormValues.jobTitle.trim(),
        whatsappNumber: this.businessFormValues.whatsappNumber.trim(),
      };

      await this.cardsService.updateCardContact(this.card.id, payload);
      this.card.contact_info = payload;

      console.log('✅ Business info updated');
    } catch (err: any) {
      console.error('❌ Failed to save business info:', err);
      alert('Failed to save business info: ' + (err.message || 'Unknown error'));
    } finally {
      this.businessSaving = false;
    }
  }



  confirmDeleteCard() {
    if (!this.card) return;
    const confirmed = confirm(
      '⚠️ Are you sure you want to delete this card?\nAll products, services, events, gallery, charity, reviews, and payments will be permanently deleted. This action cannot be undone.'
    );
    if (confirmed) this.deleteCard(this.card.id);
  }

  async deleteCard(cardId: string) {
    try {
      const success = await this.cardsService.deleteCard(cardId);
      if (success) {
        alert('Card deleted successfully!');
        this.navigateBack();
      }
    } catch (err: any) {
      console.error('Failed to delete card:', err);
      alert('Failed to delete card: ' + (err.message || 'Unknown error'));
    }
  }



  openThemePicker(): void {
    const cardId = this.card?.id;
    if (!cardId) {
      this.error = 'Card ID is missing. Cannot select theme.';
      console.warn('Card ID not available for theme selection');
      return;
    }

    const dialogRef = this.dialog.open(ThemePickerComponent, {
      width: '700px', // compact width
      maxWidth: '95vw', // never exceed viewport width
      height: 'auto', // height adjusts to content
      maxHeight: '90vh', // prevent overflow
      panelClass: 'theme-picker-dialog',
      autoFocus: false,
    });

    dialogRef.componentInstance.cardId = cardId;

    dialogRef.componentInstance.themeSelected.subscribe((theme) => {
      const gradient = `linear-gradient(135deg, ${theme.startColor}, ${theme.endColor})`;

      // Example: store or preview the theme
      // this.previewTheme = gradient;

      // Apply + persist in ThemePicker itself
      dialogRef.componentInstance.apply();

      dialogRef.close();
    });
  }

  navigateBack() {
    this.router.navigate(['/cards']);
  }


  // Toggle display dropdowns for quick/social links
  toggleLinksDropdown(which: 'quick' | 'social') {
    this.linksOpen[which] = !this.linksOpen[which];
  }


  toggleSection(
    section:
      | 'contact'
      | 'card'
      | 'business'
      | 'socialLinks'
      | 'quickLinks'
      | 'logo'
      | 'theme'
      | 'delete'
      | 'entities'
      | 'leaders'
      | 'permissions'
      | 'branches'
      | null
  ) {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

}
